import { useEffect, useRef, useState } from "react";
import {
  TextOverlay,
  LogoOverlay,
  // WatermarkOverlay,
  SlateOverlay,
  DragState,
  ResizeState,
  TextEditingState,
  TextResizeState,
} from "../types/overlayTypes";
import { calculateTextLines } from "../utils/textCalculations";

export interface TextRotationState {
  isRotating: boolean;
  textOverlayId?: string;
  startRotation: number;
  startX: number;
  startY: number;
  centerX: number;
  centerY: number;
}
export const useCanvasRendering = (
  canvasRef: React.RefObject<HTMLCanvasElement>,
  textOverlays: TextOverlay[],
  logoOverlays: LogoOverlay[],
  // watermarkOverlays: WatermarkOverlay[],
  slateOverlays: SlateOverlay[],
  dragState: DragState,
  resizeState: ResizeState,
  textEditingState: TextEditingState,
  textResizeState: TextResizeState,
  textRotationState: TextRotationState,
  showCanvasOverlay: boolean,
  logoImagesRef: React.MutableRefObject<Map<string, HTMLImageElement>>,
  videoDimensions?: { width: number; height: number }, // New parameter
  readOnlyMode?: boolean, // New parameter to hide resize handles
  selectedTextOverlayId?: string, // New parameter for selected text overlay
  selectedLogoOverlayId?: string // New parameter for selected logo overlay
) => {
  const animationFrameRef = useRef<number | null>(null);

  // Utility functions and constants
  const RESIZE_HANDLE_SIZE = 8;

  const getCornerPositions = (
    elementX: number,
    elementY: number,
    logoWidth: number,
    logoHeight: number
  ) => [
    { x: elementX - logoWidth / 2, y: elementY - logoHeight / 2 },
    { x: elementX + logoWidth / 2, y: elementY - logoHeight / 2 },
    { x: elementX - logoWidth / 2, y: elementY + logoHeight / 2 },
    { x: elementX + logoWidth / 2, y: elementY + logoHeight / 2 },
  ];

  const getRotationHandlePosition = (
    elementX: number,
    elementY: number,
    elementHeight: number,
    rotation = 0
  ) => {
    const handleDistance = elementHeight / 2 + 30;
    return {
      x: elementX,
      y: elementY - handleDistance,
    };
  };

  const loadLogoImage = (logoOverlay: any, onLoad?: () => void) => {
    const existingImage = logoImagesRef.current.get(logoOverlay.id);
    if (existingImage && existingImage.src === logoOverlay.url) {
      onLoad?.();
      return existingImage;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      logoImagesRef.current.set(logoOverlay.id, img);
      onLoad?.();
    };
    img.onerror = (error) => {
      console.error("Failed to load logo image:", logoOverlay.url, error);
    };
    img.src = logoOverlay.url;
    return img;
  };

  // const preloadAllLogoImages = async () => {
  // const logoLoadPromises = logoOverlays
  //   .filter((overlay) => overlay.visible && overlay.url)
  //   .map(async (logoOverlay) => {
  //     const existingImg = logoImagesRef.current.get(logoOverlay.id);
  //     if (existingImg && existingImg.complete) {
  //       return existingImg;
  //     }
  //     return new Promise<HTMLImageElement>((resolve, reject) => {
  //       const img = new Image();
  //       img.crossOrigin = 'anonymous';
  //       img.onload = () => {
  //         logoImagesRef.current.set(logoOverlay.id, img);
  //         resolve(img);
  //       };
  //       img.onerror = (error) => {
  //         reject(new Error(`Failed to load logo: ${logoOverlay.url}`));
  //       };
  //       img.src = logoOverlay.url;
  //     });
  //   });
  // };

  const hasOverlayContent = () => {
    if (showCanvasOverlay) {
      return true;
    }

    return (
      textOverlays.some((overlay) => overlay.visible && overlay.text) ||
      logoOverlays.some((overlay) => overlay.visible && overlay.url) ||
      // watermarkOverlays.some((overlay) => overlay.visible && overlay.url) ||
      slateOverlays.some((overlay) => overlay.visible && overlay.url)
    );
  };

  const applyRotation = (
    ctx: CanvasRenderingContext2D,
    position: { x: number; y: number },
    rotation: number
  ) => {
    if (rotation && rotation !== 0) {
      ctx.save();
      ctx.translate(position.x, position.y);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.translate(-position.x, -position.y);
      return true;
    }
    return false;
  };

  const restoreRotation = (
    ctx: CanvasRenderingContext2D,
    wasRotated: boolean
  ) => {
    if (wasRotated) {
      ctx.restore();
    }
  };

  const drawRotationHandle = (
    ctx: CanvasRenderingContext2D,
    position: { x: number; y: number },
    backgroundHeight: number,
    rotation: number
  ) => {
    const rotationHandle = getRotationHandlePosition(
      position.x,
      position.y,
      backgroundHeight,
      rotation
    );

    ctx.fillStyle = "#00ff00";
    ctx.beginPath();
    ctx.arc(
      rotationHandle.x,
      rotationHandle.y,
      RESIZE_HANDLE_SIZE / 2,
      0,
      2 * Math.PI
    );
    ctx.fill();
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(
      rotationHandle.x,
      rotationHandle.y,
      RESIZE_HANDLE_SIZE / 3,
      -Math.PI / 4,
      Math.PI / 4
    );
    ctx.stroke();

    // Draw arrow head
    ctx.beginPath();
    ctx.moveTo(
      rotationHandle.x + (RESIZE_HANDLE_SIZE / 3) * Math.cos(Math.PI / 4),
      rotationHandle.y + (RESIZE_HANDLE_SIZE / 3) * Math.sin(Math.PI / 4)
    );
    ctx.lineTo(
      rotationHandle.x + (RESIZE_HANDLE_SIZE / 3) * Math.cos(Math.PI / 4) - 3,
      rotationHandle.y + (RESIZE_HANDLE_SIZE / 3) * Math.sin(Math.PI / 4) - 3
    );
    ctx.lineTo(
      rotationHandle.x + (RESIZE_HANDLE_SIZE / 3) * Math.cos(Math.PI / 4) + 3,
      rotationHandle.y + (RESIZE_HANDLE_SIZE / 3) * Math.sin(Math.PI / 4) - 3
    );
    ctx.fill();
  };

  const drawTextResizeHandles = (
    ctx: CanvasRenderingContext2D,
    backgroundX: number,
    backgroundY: number,
    backgroundWidth: number,
    backgroundHeight: number,
    rotation = 0,
    centerX = 0,
    centerY = 0
  ) => {
    const center =
      centerX && centerY
        ? { x: centerX, y: centerY }
        : {
            x: backgroundX + backgroundWidth / 2,
            y: backgroundY + backgroundHeight / 2,
          };
    const halfWidth = backgroundWidth / 2;
    const halfHeight = backgroundHeight / 2;
    const unrotatedCorners = [
      { x: -halfWidth, y: -halfHeight }, // top-left
      { x: halfWidth, y: -halfHeight }, // top-right
      { x: -halfWidth, y: halfHeight }, // bottom-left
      { x: halfWidth, y: halfHeight }, // bottom-right
    ];
    const rotationRad = (rotation * Math.PI) / 180;
    const corners = unrotatedCorners.map(({ x: relX, y: relY }) => {
      if (rotation && rotation !== 0) {
        const cosRot = Math.cos(rotationRad);
        const sinRot = Math.sin(rotationRad);
        const rotatedX = relX * cosRot - relY * sinRot;
        const rotatedY = relX * sinRot + relY * cosRot;
        return {
          x: center.x + rotatedX,
          y: center.y + rotatedY,
        };
      } else {
        return {
          x: center.x + relX,
          y: center.y + relY,
        };
      }
    });

    corners.forEach(({ x: cornerX, y: cornerY }) => {
      ctx.fillStyle = "#00ff00";
      ctx.fillRect(
        cornerX - RESIZE_HANDLE_SIZE / 2,
        cornerY - RESIZE_HANDLE_SIZE / 2,
        RESIZE_HANDLE_SIZE,
        RESIZE_HANDLE_SIZE
      );
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 2;
      ctx.strokeRect(
        cornerX - RESIZE_HANDLE_SIZE / 2,
        cornerY - RESIZE_HANDLE_SIZE / 2,
        RESIZE_HANDLE_SIZE,
        RESIZE_HANDLE_SIZE
      );
    });
  };

  const isTextOverlaySelected = (textOverlay: any) => {
    return (
      (dragState.isDragging &&
        dragState.element === "text" &&
        dragState.textOverlayId === textOverlay.id) ||
      (textEditingState.isEditing &&
        textEditingState.textOverlayId === textOverlay.id) ||
      (textResizeState.isResizing &&
        textResizeState.textOverlayId === textOverlay.id) ||
      selectedTextOverlayId === textOverlay.id
    );
  };

  const getTextBorderColor = (textOverlay: any) => {
    return textResizeState.isResizing &&
      textResizeState.textOverlayId === textOverlay.id
      ? "#ff6b35"
      : "#00ff00";
  };

  const getCanvasScaling = () => {
    const videoElement = document.querySelector("video");
    if (videoElement && !videoElement.crossOrigin) {
      videoElement.crossOrigin = "anonymous";
    }

    const videoWidth =
      videoDimensions?.width || videoElement?.videoWidth || 1920;
    const videoHeight =
      videoDimensions?.height || videoElement?.videoHeight || 1080;
    const canvasAspectRatio =
      canvasRef.current!.width / canvasRef.current!.height;
    const videoAspectRatio = videoWidth / videoHeight;

    let scaleX = 1;
    let scaleY = 1;
    let offsetX = 0;
    let offsetY = 0;

    if (canvasAspectRatio > videoAspectRatio) {
      scaleY = canvasRef.current!.height / videoHeight;
      scaleX = scaleY;
      offsetX = (canvasRef.current!.width - videoWidth * scaleX) / 2;
    } else {
      scaleX = canvasRef.current!.width / videoWidth;
      scaleY = scaleX;
      offsetY = (canvasRef.current!.height - videoHeight * scaleY) / 2;
    }

    return { scaleX, scaleY, offsetX, offsetY, videoWidth, videoHeight };
  };

  const getCanvasPosition = (
    percentX: number,
    percentY: number,
    scaleX: number,
    scaleY: number,
    offsetX: number,
    offsetY: number,
    videoWidth: number,
    videoHeight: number
  ) => {
    const videoX = (percentX / 100) * videoWidth;
    const videoY = (percentY / 100) * videoHeight;
    return {
      x: Math.round(videoX * scaleX + offsetX),
      y: Math.round(videoY * scaleY + offsetY),
    };
  };

  const drawImageOverlay = (
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement,
    overlay: any,
    position: { x: number; y: number },
    width: number,
    height: number
  ) => {
    const drawX = position.x - width / 2;
    const drawY = position.y - height / 2;

    ctx.globalAlpha = overlay.opacity !== undefined ? overlay.opacity : 1.0;

    const wasRotated = applyRotation(ctx, position, overlay.rotation);
    ctx.drawImage(img, drawX, drawY, width, height);
    restoreRotation(ctx, wasRotated);
    ctx.globalAlpha = 1.0;
  };

  const drawSelectionBorder = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    color = "#00ff00"
  ) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    ctx.strokeRect(x - 5, y - 5, width + 10, height + 10);
    ctx.setLineDash([]);
  };

  const drawResizeHandles = (
    ctx: CanvasRenderingContext2D,
    position: { x: number; y: number },
    width: number,
    height: number
  ) => {
    const handleColor = "#00ff00";
    const handleStrokeColor = "#ffffff";
    const corners = getCornerPositions(position.x, position.y, width, height);

    corners.forEach(({ x: cornerX, y: cornerY }) => {
      ctx.fillStyle = handleColor;
      ctx.fillRect(
        cornerX - RESIZE_HANDLE_SIZE / 2,
        cornerY - RESIZE_HANDLE_SIZE / 2,
        RESIZE_HANDLE_SIZE,
        RESIZE_HANDLE_SIZE
      );
      ctx.strokeStyle = handleStrokeColor;
      ctx.lineWidth = 2;
      ctx.strokeRect(
        cornerX - RESIZE_HANDLE_SIZE / 2,
        cornerY - RESIZE_HANDLE_SIZE / 2,
        RESIZE_HANDLE_SIZE,
        RESIZE_HANDLE_SIZE
      );
    });
  };

  const renderImageOverlays = (
    ctx: CanvasRenderingContext2D,
    overlays: any[],
    overlayType: string,
    scaleX: number,
    scaleY: number,
    offsetX: number,
    offsetY: number,
    videoWidth: number,
    videoHeight: number
  ) => {
    overlays.forEach((overlay) => {
      if (overlay.visible && overlay.url) {
        const img = logoImagesRef.current.get(overlay.id);
        if (img && img.complete) {
          const position = getCanvasPosition(
            overlay.position.x,
            overlay.position.y,
            scaleX,
            scaleY,
            offsetX,
            offsetY,
            videoWidth,
            videoHeight
          );
          const overlayWidth = overlay.size.width * scaleX;
          const overlayHeight = overlay.size.height * scaleY;

          drawImageOverlay(
            ctx,
            img,
            overlay,
            position,
            overlayWidth,
            overlayHeight
          );
          if (
            (dragState.isDragging &&
              dragState.element === overlayType &&
              dragState.logoOverlayId === overlay.id) ||
            selectedLogoOverlayId === overlay.id
          ) {
            drawSelectionBorder(
              ctx,
              position.x - overlayWidth / 2,
              position.y - overlayHeight / 2,
              overlayWidth,
              overlayHeight
            );
          }

          if (
            showCanvasOverlay &&
            overlay.visible &&
            overlay.url &&
            !readOnlyMode &&
            selectedLogoOverlayId === overlay.id
          ) {
            drawResizeHandles(ctx, position, overlayWidth, overlayHeight);
          }
        } else {
          loadLogoImage(overlay, () => {
            if (canvasRef.current) {
              renderCanvas();
            }
          });
        }
      }
    });
  };

  const calculateTextRendering = (
    ctx: CanvasRenderingContext2D,
    textOverlay: any,
    options: {
      isHighRes: boolean;
      videoWidth: number;
      videoHeight: number;
      scaleX?: number;
      scaleY?: number;
      scaleRatioX?: number;
      scaleRatioY?: number;
      offsetX?: number;
      offsetY?: number;
    }
  ) => {
    const {
      isHighRes,
      videoWidth,
      videoHeight,
      scaleX = 1,
      scaleY = 1,
      scaleRatioX = 1,
      scaleRatioY = 1,
      offsetX = 0,
      offsetY = 0,
    } = options;

    const displayText =
      textOverlay.text && textOverlay.text.trim() !== ""
        ? textOverlay.text
        : "New Text";

    // Calculate position based on rendering mode
    let position;
    if (isHighRes) {
      // High-res: direct video coordinates
      position = {
        x: (textOverlay.position.x / 100) * videoWidth,
        y: (textOverlay.position.y / 100) * videoHeight,
      };
    } else {
      // Real-time: canvas coordinates with scaling
      const videoX = (textOverlay.position.x / 100) * videoWidth;
      const videoY = (textOverlay.position.y / 100) * videoHeight;
      position = {
        x: videoX * scaleX + offsetX,
        y: videoY * scaleY + offsetY,
      };
    }

    // Calculate font size based on rendering mode
    let fontSize = textOverlay.fontSize;

    if (isHighRes && canvasRef.current) {
      const canvas = canvasRef.current;
      const canvasRect = canvas.getBoundingClientRect();
      const scaleFactor = Math.max(
        videoWidth / canvasRect.width,
        videoHeight / canvasRect.height
      );
      fontSize = textOverlay.fontSize * scaleFactor;
    }

    // Calculate max width based on rendering mode
    const maxWidth = isHighRes
      ? videoWidth
      : videoWidth * Math.min(scaleX, scaleY);

    // Set font properties
    const fontStyle = textOverlay.isItalic ? "italic " : "";
    const fontWeight = textOverlay.isBold ? "bold" : "normal";
    const fontFamily = textOverlay.fontFamily || "Arial";
    ctx.font = `${fontStyle}${fontWeight} ${fontSize}px ${fontFamily}`;

    const padding = textOverlay.padding || 0;

    // Calculate text dimensions with wrapping using shared utility
    const lines = calculateTextLines(ctx, displayText, maxWidth);

    // Calculate dimensions
    const lineHeight = fontSize * 1.2;
    const textHeight = lines.length * lineHeight;
    const textWidth = Math.max(
      ...lines.map((line) => ctx.measureText(line).width)
    );
    const paddedWidth = textWidth + padding * 2;
    const paddedHeight = textHeight + padding * 2;

    return {
      displayText,
      position,
      fontSize,
      maxWidth,
      textWidth,
      textHeight,
      paddedWidth,
      paddedHeight,
      lines,
      lineHeight,
    };
  };

  const renderTextOverlayUnified = (
    ctx: CanvasRenderingContext2D,
    textOverlay: any,
    options: {
      isHighRes: boolean;
      videoWidth: number;
      videoHeight: number;
      scaleX?: number;
      scaleY?: number;
      scaleRatioX?: number;
      scaleRatioY?: number;
      offsetX?: number;
      offsetY?: number;
    }
  ) => {
    if (!textOverlay.visible) return;

    // Apply opacity
    const opacity =
      textOverlay.opacity !== undefined ? textOverlay.opacity : 1.0;
    ctx.globalAlpha = opacity;

    // Get all text rendering calculations from unified function
    const {
      displayText,
      position,
      fontSize,
      maxWidth,
      textWidth,
      textHeight,
      paddedWidth,
      paddedHeight,
      lines,
      lineHeight,
    } = calculateTextRendering(ctx, textOverlay, options);

    // Set text properties
    ctx.fillStyle = textOverlay.fontColor;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    // Apply rotation
    const wasRotated = applyRotation(ctx, position, textOverlay.rotation);

    // Draw background
    if (
      textOverlay.backgroundColor &&
      textOverlay.backgroundColor !== "transparent"
    ) {
      ctx.fillStyle = textOverlay.backgroundColor;
      const backgroundWidth = paddedWidth + 16;
      const backgroundHeight = paddedHeight + 16;
      ctx.fillRect(
        position.x - backgroundWidth / 2,
        position.y - backgroundHeight / 2,
        backgroundWidth,
        backgroundHeight
      );
    }

    // Draw text with effects using unified calculation
    ctx.fillStyle = textOverlay.fontColor;
    ctx.shadowColor = "#000000";
    ctx.shadowBlur = 4;
    ctx.shadowOffsetX = 2;
    ctx.shadowOffsetY = 2;

    // Draw each line using the calculated lines array
    const totalHeight = lines.length * lineHeight;
    const startY = position.y - (totalHeight - lineHeight) / 2;

    lines.forEach((line, index) => {
      const y = startY + index * lineHeight;
      ctx.fillText(line, position.x, y);
    });

    // Draw underline for the last line only
    if (textOverlay.isUnderline && lines.length > 0) {
      const lastLineWidth = ctx.measureText(lines[lines.length - 1]).width;
      ctx.strokeStyle = textOverlay.fontColor;
      ctx.lineWidth = Math.max(1, fontSize / 20);
      ctx.beginPath();
      ctx.moveTo(
        position.x - lastLineWidth / 2,
        startY + (lines.length - 1) * lineHeight + fontSize / 2 + 2
      );
      ctx.lineTo(
        position.x + lastLineWidth / 2,
        startY + (lines.length - 1) * lineHeight + fontSize / 2 + 2
      );
      ctx.stroke();
    }

    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;

    // Restore rotation
    restoreRotation(ctx, wasRotated);

    // Reset global alpha
    ctx.globalAlpha = 1.0;

    // Draw selection border and handles for real-time only
    if (!options.isHighRes) {
      if (isTextOverlaySelected(textOverlay)) {
        const wasRotatedForBorder = applyRotation(
          ctx,
          position,
          textOverlay.rotation
        );
        const backgroundX = position.x - paddedWidth / 2 - 8;
        const backgroundY = position.y - paddedHeight / 2 - 4;
        const backgroundWidth = paddedWidth + 16;
        const backgroundHeight = paddedHeight + 8;

        const borderColor = getTextBorderColor(textOverlay);
        drawSelectionBorder(
          ctx,
          backgroundX,
          backgroundY,
          backgroundWidth,
          backgroundHeight,
          borderColor
        );

        restoreRotation(ctx, wasRotatedForBorder);
      }

      if (
        showCanvasOverlay &&
        textOverlay.visible &&
        !readOnlyMode &&
        isTextOverlaySelected(textOverlay)
      ) {
        const backgroundX = position.x - paddedWidth / 2 - 8;
        const backgroundY = position.y - paddedHeight / 2 - 4;
        const backgroundWidth = paddedWidth + 16;
        const backgroundHeight = paddedHeight + 8;

        drawTextResizeHandles(
          ctx,
          backgroundX,
          backgroundY,
          backgroundWidth,
          backgroundHeight,
          textOverlay.rotation || 0,
          position.x,
          position.y
        );
      }
    }
  };

  const renderCanvas = () => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const { scaleX, scaleY, offsetX, offsetY, videoWidth, videoHeight } =
      getCanvasScaling();

    // Draw white border instead of red
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2;
    ctx.strokeRect(offsetX, offsetY, videoWidth * scaleX, videoHeight * scaleY);
    // Remove the red fill/shadow
    ctx.fillStyle = "#000000";

    renderImageOverlays(
      ctx,
      logoOverlays,
      "logo",
      scaleX,
      scaleY,
      offsetX,
      offsetY,
      videoWidth,
      videoHeight
    );
    // renderImageOverlays(ctx, watermarkOverlays, 'watermark', scaleX, scaleY, offsetX, offsetY, videoWidth, videoHeight);
    renderImageOverlays(
      ctx,
      slateOverlays,
      "slate",
      scaleX,
      scaleY,
      offsetX,
      offsetY,
      videoWidth,
      videoHeight
    );

    textOverlays.forEach((textOverlay) => {
      renderTextOverlayUnified(ctx, textOverlay, {
        isHighRes: false,
        videoWidth,
        videoHeight,
        scaleX,
        scaleY,
        offsetX,
        offsetY,
      });
    });
  };

  // Function to capture canvas at original video resolution
  const captureHighResCanvas = async () => {
    const videoElement = document.querySelector("video");
    if (videoElement && !videoElement.crossOrigin) {
      videoElement.crossOrigin = "anonymous";
    }

    const videoWidth =
      videoDimensions?.width || videoElement?.videoWidth || 1920;
    const videoHeight =
      videoDimensions?.height || videoElement?.videoHeight || 1080;

    const currentCanvas = canvasRef.current;
    if (!currentCanvas) {
      console.error("No canvas reference available for capture");
      return null;
    }
    const currentCanvasWidth = currentCanvas.width;
    const currentCanvasHeight = currentCanvas.height;

    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = videoWidth;
    tempCanvas.height = videoHeight;
    const tempCtx = tempCanvas.getContext("2d");

    if (!tempCtx) {
      console.error("Failed to get 2D context for temp canvas");
      return null;
    }
    const visibleLogoOverlays = logoOverlays.filter(
      (overlay) => overlay.visible && overlay.url
    );
    const visibleSlateOverlays = slateOverlays.filter(
      (overlay) => overlay.visible && overlay.url
    );

    if (visibleLogoOverlays.length > 0 || visibleSlateOverlays.length > 0) {
      const allImagesReady = [
        ...visibleLogoOverlays,
        ...visibleSlateOverlays,
      ].every((overlay) => {
        const img = logoImagesRef.current.get(overlay.id);
        return img && img.complete && img.naturalWidth > 0;
      });

      if (!allImagesReady) {
        await new Promise((resolve) => {
          const checkImages = () => {
            const ready = [
              ...visibleLogoOverlays,
              ...visibleSlateOverlays,
            ].every((overlay) => {
              const img = logoImagesRef.current.get(overlay.id);
              return img && img.complete && img.naturalWidth > 0;
            });
            if (ready) {
              resolve(true);
            } else {
              setTimeout(checkImages, 100);
            }
          };
          checkImages();
        });
      }
    }

    tempCtx.clearRect(0, 0, videoWidth, videoHeight);
    const scaleRatioX = videoWidth / currentCanvasWidth;
    const scaleRatioY = videoHeight / currentCanvasHeight;

    const getVideoPosition = (percentX: number, percentY: number) => {
      return {
        x: (percentX / 100) * videoWidth,
        y: (percentY / 100) * videoHeight,
      };
    };

    logoOverlays.forEach((logoOverlay) => {
      if (logoOverlay.visible && logoOverlay.url) {
        const img = logoImagesRef.current.get(logoOverlay.id);
        if (img && img.complete) {
          const position = getVideoPosition(
            logoOverlay.position.x,
            logoOverlay.position.y
          );
          const logoWidth = logoOverlay.size.width;
          const logoHeight = logoOverlay.size.height;
          const drawX = position.x - logoWidth / 2;
          const drawY = position.y - logoHeight / 2;

          tempCtx.globalAlpha =
            logoOverlay.opacity !== undefined ? logoOverlay.opacity : 1.0;
          if (logoOverlay.rotation && logoOverlay.rotation !== 0) {
            tempCtx.save();
            tempCtx.translate(position.x, position.y);
            tempCtx.rotate((logoOverlay.rotation * Math.PI) / 180);
            tempCtx.translate(-position.x, -position.y);
          }

          tempCtx.drawImage(img, drawX, drawY, logoWidth, logoHeight);
          if (logoOverlay.rotation && logoOverlay.rotation !== 0) {
            tempCtx.restore();
          }

          tempCtx.globalAlpha = 1.0;
        }
      }
    });

    // watermarkOverlays.forEach((watermarkOverlay) => {
    //   if (watermarkOverlay.visible && watermarkOverlay.url) {
    //     const img = logoImagesRef.current.get(watermarkOverlay.id);
    //     if (img && img.complete) {
    //       const position = getVideoPosition(watermarkOverlay.position.x, watermarkOverlay.position.y);
    //       // Scale the watermark size by the ratio
    //       const watermarkWidth = watermarkOverlay.size.width * scaleRatioX;
    //       const watermarkHeight = watermarkOverlay.size.height * scaleRatioY;
    //       const drawX = position.x - watermarkWidth / 2;
    //       const drawY = position.y - watermarkHeight / 2;

    //       tempCtx.globalAlpha = watermarkOverlay.opacity !== undefined ? watermarkOverlay.opacity : 0.3;
    //       if (watermarkOverlay.rotation && watermarkOverlay.rotation !== 0) {
    //         tempCtx.save();
    //         tempCtx.translate(position.x, position.y);
    //         tempCtx.rotate((watermarkOverlay.rotation * Math.PI) / 180);
    //         tempCtx.translate(-position.x, -position.y);
    //       }

    //       tempCtx.drawImage(img, drawX, drawY, watermarkWidth, watermarkHeight);
    //       if (watermarkOverlay.rotation && watermarkOverlay.rotation !== 0) {
    //         tempCtx.restore();
    //       }

    //       tempCtx.globalAlpha = 1.0;
    //     }
    //   }
    // });

    slateOverlays.forEach((slateOverlay) => {
      if (slateOverlay.visible && slateOverlay.url) {
        const img = logoImagesRef.current.get(slateOverlay.id);
        if (img && img.complete) {
          const position = getVideoPosition(
            slateOverlay.position.x,
            slateOverlay.position.y
          );
          // Scale the slate size by the ratio
          const slateWidth = slateOverlay.size.width * scaleRatioX;
          const slateHeight = slateOverlay.size.height * scaleRatioY;
          const drawX = position.x - slateWidth / 2;
          const drawY = position.y - slateHeight / 2;
          if (slateOverlay.rotation && slateOverlay.rotation !== 0) {
            tempCtx.save();
            tempCtx.translate(position.x, position.y);
            tempCtx.rotate((slateOverlay.rotation * Math.PI) / 180);
            tempCtx.translate(-position.x, -position.y);
          }

          // Apply rotation if specified
          if (slateOverlay.rotation && slateOverlay.rotation !== 0) {
            tempCtx.save();
            tempCtx.translate(position.x, position.y);
            tempCtx.rotate((slateOverlay.rotation * Math.PI) / 180);
            tempCtx.translate(-position.x, -position.y);
          }

          tempCtx.drawImage(img, drawX, drawY, slateWidth, slateHeight);
          if (slateOverlay.rotation && slateOverlay.rotation !== 0) {
            tempCtx.restore();
          }
        }
      }
    });

    // Use current text overlays for high-res capture
    const actualTextOverlays = textOverlays.filter(
      (t) => t.visible && t.text && t.text.trim() !== ""
    );

    actualTextOverlays.forEach((textOverlay) => {
      tempCtx.globalAlpha =
        textOverlay.opacity !== undefined ? textOverlay.opacity : 1.0;

      renderTextOverlayUnified(tempCtx, textOverlay, {
        isHighRes: true,
        videoWidth,
        videoHeight,
        scaleRatioX,
        scaleRatioY,
      });

      tempCtx.globalAlpha = 1.0;
    });

    return tempCanvas;
  };

  const stopCanvasRendering = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  useEffect(() => {
    if (hasOverlayContent() && canvasRef.current) {
      const canvas = canvasRef.current;
      const container = canvas.parentElement;
      if (container) {
        const resizeCanvas = () => {
          const rect = container.getBoundingClientRect();
          canvas.width = rect.width;
          canvas.height = rect.height;
          canvas.style.width = `${rect.width}px`;
          canvas.style.height = `${rect.height}px`;
          renderCanvas();
        };
        resizeCanvas();
        window.addEventListener("resize", resizeCanvas);
        const resizeObserver = new ResizeObserver(resizeCanvas);
        resizeObserver.observe(container);

        return () => {
          window.removeEventListener("resize", resizeCanvas);
          resizeObserver.disconnect();
        };
      }
    }
  }, [textOverlays, logoOverlays, slateOverlays, dragState]);

  useEffect(() => {
    if (hasOverlayContent()) {
      const renderLoop = () => {
        if (hasOverlayContent() && canvasRef.current) {
          renderCanvas();
          animationFrameRef.current = requestAnimationFrame(renderLoop);
        } else {
          stopCanvasRendering();
        }
      };

      renderLoop();
    } else {
      stopCanvasRendering();
    }

    return () => {
      stopCanvasRendering();
    };
  }, [textOverlays, logoOverlays, slateOverlays]);

  return {
    renderCanvas,
    captureHighResCanvas,
    // preloadAllLogoImages,
  };
};
